import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function SystemSettings({ user, onLogout }) {
  const [settings, setSettings] = useState({
    two_wheeler_rate_per_km: 3.0,
    four_wheeler_rate_per_km: 6.5,
    free_distance_km: 15.0
  });
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/settings/travel-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        two_wheeler_rate_per_km: response.data.two_wheeler_rate_per_km,
        four_wheeler_rate_per_km: response.data.four_wheeler_rate_per_km,
        free_distance_km: response.data.free_distance_km
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: parseFloat(value) });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/settings/travel-rates`,
        settings,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('✅ Settings updated successfully!');
      setHasChanges(false);
      fetchSettings();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    fetchSettings();
    setHasChanges(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Travel Rates Configuration</h1>
          <p className="mt-2 text-gray-600">Configure travel reimbursement rates for expense claims</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-6">Travel Claim Rates</h2>

          <div className="space-y-6">
            {/* 2-Wheeler Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🛵 2-Wheeler Rate (per KM)
              </label>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-gray-900">₹</span>
                <input
                  type="number"
                  step="0.01"
                  value={settings.two_wheeler_rate_per_km}
                  onChange={(e) => handleChange('two_wheeler_rate_per_km', e.target.value)}
                  className="flex-1 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                />
                <span className="text-gray-600">/ km</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Current: ₹{settings.two_wheeler_rate_per_km}/km</p>
            </div>

            {/* 4-Wheeler Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🚗 4-Wheeler Rate (per KM)
              </label>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-gray-900">₹</span>
                <input
                  type="number"
                  step="0.01"
                  value={settings.four_wheeler_rate_per_km}
                  onChange={(e) => handleChange('four_wheeler_rate_per_km', e.target.value)}
                  className="flex-1 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                />
                <span className="text-gray-600">/ km</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Current: ₹{settings.four_wheeler_rate_per_km}/km</p>
            </div>

            {/* Free Distance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📍 Free Distance (No Claim)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  step="0.1"
                  value={settings.free_distance_km}
                  onChange={(e) => handleChange('free_distance_km', e.target.value)}
                  className="flex-1 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                />
                <span className="text-gray-600">km</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Employees can travel up to {settings.free_distance_km} km (round trip) from their office location without claiming
              </p>
            </div>

            {/* Preview Calculation */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-3">💡 Example Calculation</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>Scenario:</strong> Employee travels 25 km to client location</p>
                <p>Total Distance: 25 km × 2 (round trip) = 50 km</p>
                <p>Free Distance: {settings.free_distance_km} km</p>
                <p>Claimable: 50 km - {settings.free_distance_km} km = {50 - settings.free_distance_km} km</p>
                <p><strong>2-Wheeler Claim:</strong> {50 - settings.free_distance_km} km × ₹{settings.two_wheeler_rate_per_km} = ₹{((50 - settings.free_distance_km) * settings.two_wheeler_rate_per_km).toFixed(2)}</p>
                <p><strong>4-Wheeler Claim:</strong> {50 - settings.free_distance_km} km × ₹{settings.four_wheeler_rate_per_km} = ₹{((50 - settings.free_distance_km) * settings.four_wheeler_rate_per_km).toFixed(2)}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleReset}
                disabled={!hasChanges || loading}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Changes
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || loading}
                className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? 'Saving...' : '💾 Save Settings'}
              </button>
            </div>

            {hasChanges && (
              <p className="text-sm text-yellow-600 text-center">
                ⚠️ You have unsaved changes
              </p>
            )}
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h3>
            <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
              <li>Changes will affect all future travel claims</li>
              <li>Existing claims will not be recalculated</li>
              <li>Only admins can modify these settings</li>
              <li>Rates should comply with company travel policy</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SystemSettings;